import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as S from "./IASistemasStyle";
import { iaService } from "../../Services/iaService";

/**
 * Aba de Tickets de Suporte (criados pela Ana durante os atendimentos).
 * Busca por texto (cliente, título, descrição, nº), filtros de status e
 * urgência, paginação, detalhes e mudança de status.
 * Estado persistido na URL: ?tab=tickets&ts=1&urg=urgente&tq=maria&pg=2
 */

const STATUS = {
  1: { label: "Pendente", tipo: "pendente" },
  2: { label: "Em análise", tipo: "analise" },
  3: { label: "Resolvido", tipo: "resolvido" },
  4: { label: "Cancelado", tipo: "cancelado" },
};

const URGENCIA = {
  urgente: { label: "Urgente", tipo: "urgente" },
  prioritario: { label: "Prioritário", tipo: "pendente" },
  normal: { label: "Normal", tipo: "cancelado" },
};

export default function TicketsTab() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [tickets, setTickets] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [stats, setStats] = useState(null);

  const [search, setSearch] = useState(searchParams.get("tq") || "");
  const [status, setStatus] = useState(searchParams.get("ts") || "");
  const [urgencia, setUrgencia] = useState(searchParams.get("urg") || "");
  const page = Number(searchParams.get("pg")) || 1;

  const syncUrl = useCallback(
    (updates) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === undefined || v === null || v === "" ) p.delete(k);
            else p.set(k, String(v));
          });
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await iaService.getTickets({
        page,
        limit: 15,
        status: status || undefined,
        emergency_level: urgencia || undefined,
        search: search || undefined,
      });
      setTickets(res.data || []);
      setPagination(res.pagination || null);
    } catch (e) {
      console.error("Erro ao listar tickets:", e);
    } finally {
      setLoading(false);
    }
  }, [page, status, urgencia, search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    iaService
      .getDashboardStats()
      .then((s) => setStats(s.tickets || null))
      .catch(() => {});
  }, []);

  /** Cartões clicáveis: clicar filtra; clicar de novo limpa o filtro. */
  const toggleStatus = (value) => {
    const novo = status === value ? "" : value;
    setStatus(novo);
    syncUrl({ ts: novo, pg: "" });
  };
  const toggleUrgencia = (value) => {
    const novo = urgencia === value ? "" : value;
    setUrgencia(novo);
    syncUrl({ urg: novo, pg: "" });
  };

  const handleStatusChange = async (ticket, novoStatus) => {
    const st = STATUS[novoStatus];
    if (!window.confirm(`Alterar o ticket #${ticket.id} para "${st.label}"?`)) return;
    try {
      await iaService.setTicketStatus(ticket.id, Number(novoStatus));
      await load();
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao alterar status do ticket.");
    }
  };

  const totalPages = pagination?.totalPages || 1;

  return (
    <S.Panel>
      <S.TicketsHeader>
        <div>
          <h3>Tickets de Suporte</h3>
          <small>Chamados abertos pela Ana durante os atendimentos</small>
        </div>
        {pagination && (
          <span className="contagem">
            {pagination.total} ticket{pagination.total === 1 ? "" : "s"} encontrado
            {pagination.total === 1 ? "" : "s"}
          </span>
        )}
      </S.TicketsHeader>

      {stats && (
        <S.TicketStats>
          <S.TicketStatCard
            $cor="#b54708"
            $active={status === "1"}
            onClick={() => toggleStatus("1")}
          >
            <label>Pendentes</label>
            <strong>{stats.pendentes}</strong>
            <span>{status === "1" ? "filtrando ✕" : "clique p/ filtrar"}</span>
          </S.TicketStatCard>
          <S.TicketStatCard
            $cor="#1d4ed8"
            $active={status === "2"}
            onClick={() => toggleStatus("2")}
          >
            <label>Em análise</label>
            <strong>{stats.em_analise}</strong>
            <span>{status === "2" ? "filtrando ✕" : "clique p/ filtrar"}</span>
          </S.TicketStatCard>
          <S.TicketStatCard
            $cor="#087443"
            $active={status === "3"}
            onClick={() => toggleStatus("3")}
          >
            <label>Resolvidos</label>
            <strong>{stats.resolvidos}</strong>
            <span>{status === "3" ? "filtrando ✕" : "clique p/ filtrar"}</span>
          </S.TicketStatCard>
          <S.TicketStatCard
            $cor="#b42318"
            $active={urgencia === "urgente"}
            onClick={() => toggleUrgencia("urgente")}
          >
            <label>Urgentes em aberto</label>
            <strong>{stats.urgentes_em_aberto}</strong>
            <span>{urgencia === "urgente" ? "filtrando ✕" : "clique p/ filtrar"}</span>
          </S.TicketStatCard>
        </S.TicketStats>
      )}

      <S.TicketsToolbar>
        <input
          placeholder="Buscar por cliente, assunto, descrição ou nº do ticket..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            syncUrl({ tq: e.target.value, pg: "" });
          }}
        />
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            syncUrl({ ts: e.target.value, pg: "" });
          }}
        >
          <option value="">Todos os status</option>
          <option value="1">Pendente</option>
          <option value="2">Em análise</option>
          <option value="3">Resolvido</option>
          <option value="4">Cancelado</option>
        </select>
        <select
          value={urgencia}
          onChange={(e) => {
            setUrgencia(e.target.value);
            syncUrl({ urg: e.target.value, pg: "" });
          }}
        >
          <option value="">Todas as urgências</option>
          <option value="urgente">Urgente</option>
          <option value="prioritario">Prioritário</option>
          <option value="normal">Normal</option>
        </select>
      </S.TicketsToolbar>

      {loading ? (
        <S.LoadingText>Carregando tickets...</S.LoadingText>
      ) : tickets.length === 0 ? (
        <S.LoadingText>Nenhum ticket encontrado com esses filtros.</S.LoadingText>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <S.Table>
            <thead>
              <tr>
                <th>#</th>
                <th>Cliente</th>
                <th>Assunto</th>
                <th>Urgência</th>
                <th>Status</th>
                <th>Contrato</th>
                <th>Criado em</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => {
                const st = STATUS[t.status] || STATUS[1];
                const urg = URGENCIA[t.emergency_level] || URGENCIA.normal;
                const isOpen = expanded === t.id;
                return (
                  <React.Fragment key={t.id}>
                    <tr
                      className={`clicavel${isOpen ? " aberta" : ""}`}
                      onClick={() => setExpanded(isOpen ? null : t.id)}
                    >
                      <td className="num">{t.id}</td>
                      <td className="titulo">{t.client_name || `Cliente ${t.client_id}`}</td>
                      <td className="titulo">{t.title || "Sem título"}</td>
                      <td>
                        <S.StatusChip $tipo={urg.tipo}>{urg.label}</S.StatusChip>
                      </td>
                      <td>
                        <S.StatusChip $tipo={st.tipo}>{st.label}</S.StatusChip>
                      </td>
                      <td>{t.contract_id ? `#${t.contract_id}` : "—"}</td>
                      <td>
                        {t.created_at
                          ? new Date(t.created_at).toLocaleDateString("pt-BR")
                          : "—"}
                      </td>
                      <td className="seta">{isOpen ? "▲" : "▼"}</td>
                    </tr>
                    {isOpen && (
                      <tr className="detalhe">
                        <td colSpan={8}>
                          <S.TicketDetail>
                            <div className="corpo">
                              <div className="descricao">
                                <label>Descrição completa</label>
                                <p>{t.description || "Sem descrição."}</p>
                              </div>
                              <div className="meta">
                                <div>
                                  <label>Cliente</label>
                                  <b>
                                    {t.client_name || "—"}{" "}
                                    {t.client_id ? `(ID ${t.client_id})` : ""}
                                  </b>
                                </div>
                                <div>
                                  <label>Aberto em</label>
                                  <b>
                                    {t.created_at
                                      ? new Date(t.created_at).toLocaleString("pt-BR")
                                      : "—"}
                                  </b>
                                </div>
                                <div>
                                  <label>Última atualização</label>
                                  <b>
                                    {t.updated_at
                                      ? new Date(t.updated_at).toLocaleString("pt-BR")
                                      : "—"}
                                  </b>
                                </div>
                                <div>
                                  <label>Tratado por</label>
                                  <b>{t.done_by_name || "—"}</b>
                                </div>
                              </div>
                            </div>
                            <div className="acoes" onClick={(e) => e.stopPropagation()}>
                              <label>Mover para:</label>
                              {Object.entries(STATUS)
                                .filter(([k]) => Number(k) !== t.status)
                                .map(([k, v]) => (
                                  <S.StatusButton
                                    key={k}
                                    $tipo={v.tipo}
                                    onClick={() => handleStatusChange(t, k)}
                                  >
                                    {v.label}
                                  </S.StatusButton>
                                ))}
                            </div>
                          </S.TicketDetail>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </S.Table>
        </div>
      )}

      {pagination && totalPages > 1 && (
        <S.Pagination>
          <button
            disabled={page <= 1}
            onClick={() => syncUrl({ pg: page - 1 })}
          >
            ← Anterior
          </button>
          <span>
            Página {page} de {totalPages} · {pagination.total} tickets
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => syncUrl({ pg: page + 1 })}
          >
            Próxima →
          </button>
        </S.Pagination>
      )}
    </S.Panel>
  );
}
